import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, StyleSheet, ScrollView, Alert, TouchableOpacity, Linking, Image } from 'react-native';
import { Text, Card, Button, ActivityIndicator, Divider, TextInput, RadioButton } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import * as FileSystem from 'expo-file-system/legacy';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import api from '../../services/api';
import SignaturePad from '../../components/SignaturePad';
import { generateTicketHTML } from '../../utils/facturaGenerator';

const DetalleEntregaScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { pedido } = route.params;
  const signatureRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [notasEntrega, setNotasEntrega] = useState('');
  const [firmaData, setFirmaData] = useState(null);
  const [firmaCapturada, setFirmaCapturada] = useState(false);
  const [firmando, setFirmando] = useState(false);

  const loadDetalle = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getRepartidorDetalle(pedido.id);
      setDetalle(data);
      if (data.metodo_pago) setMetodoPago(data.metodo_pago);
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      Alert.alert('Error', 'No se pudo cargar el detalle del pedido');
    } finally {
      setLoading(false);
    }
  }, [pedido.id]);

  useEffect(() => {
    loadDetalle();
  }, [loadDetalle]);

  const handleIniciarEntrega = () => {
    Alert.alert(
      'Iniciar Entrega',
      `¿Recoger el pedido #${pedido.id} para entrega?\n\nCliente: ${pedido.cliente_nombre}\nDirección: ${pedido.cliente_direccion || 'Sin dirección'}`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Recoger Pedido',
          onPress: async () => {
            try {
              setProcessing(true);
              await api.iniciarEntregaRepartidor(pedido.id);
              Alert.alert(
                'Pedido Recogido',
                `Pedido #${pedido.id} marcado como en ruta`,
                [{ text: 'OK', onPress: () => navigation.goBack() }]
              );
            } catch (error) {
              Alert.alert('Error', error.message);
            } finally {
              setProcessing(false);
            }
          },
        },
      ]
    );
  };

  const handleCapturarFirma = () => {
    if (signatureRef.current) {
      signatureRef.current.getSignature();
    }
  };

  const handleFirmaRecibida = (data) => {
    if (data) {
      setFirmaData(data);
      setFirmaCapturada(true);
    } else {
      Alert.alert('Firma Vacía', 'El cliente debe firmar antes de confirmar la entrega.');
    }
  };

  const handleConfirmarEntrega = () => {
    if (!firmaCapturada) {
      // Solicitar firma primero
      Alert.alert(
        'Firma Requerida',
        'El cliente debe firmar para confirmar la recepción del pedido. ¿Desea capturar la firma ahora?',
        [
          { text: 'Cancelar', style: 'cancel' },
          {
            text: 'Capturar Firma',
            onPress: handleCapturarFirma,
          },
        ]
      );
      return;
    }

    const metodoMostrado = detalle?.metodo_pago === 'credito' ? 'crédito' : metodoPago;
    Alert.alert(
      'Confirmar Entrega',
      `¿Confirmar que el pedido #${pedido.id} fue entregado al cliente?\n\nMétodo de pago: ${metodoMostrado}\nFirma: Capturada`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Confirmar Entrega',
          onPress: async () => {
            try {
              setProcessing(true);
              // Si la venta original es a crédito, el backend igual la mantiene como
              // crédito; aquí mandamos el método elegido para los demás casos.
              const metodoFinal = detalle?.metodo_pago === 'credito' ? 'credito' : metodoPago;
              await api.confirmarEntregaRepartidor(pedido.id, {
                metodo_pago: metodoFinal,
                notas: notasEntrega || `Entregado - Pago: ${metodoFinal}`,
                firma_cliente: firmaData,
              });
              Alert.alert(
                'Entrega Confirmada',
                `Pedido #${pedido.id} entregado exitosamente`,
                [{ text: 'OK', onPress: () => navigation.goBack() }]
              );
            } catch (error) {
              Alert.alert('Error', error.message);
            } finally {
              setProcessing(false);
            }
          },
        },
      ]
    );
  };

  const handleCompletarVenta = () => {
    Alert.alert(
      'Completar Venta',
      `¿Marcar el pedido #${pedido.id} como completado (pago recibido)?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Completar',
          onPress: async () => {
            try {
              setProcessing(true);
              await api.completarVentaRepartidor(pedido.id, {
                notas: 'Venta completada - pago recibido',
              });
              Alert.alert(
                'Venta Completada',
                `Pedido #${pedido.id} completado`,
                [{ text: 'OK', onPress: () => navigation.goBack() }]
              );
            } catch (error) {
              Alert.alert('Error', error.message);
            } finally {
              setProcessing(false);
            }
          },
        },
      ]
    );
  };

  const handleLlamar = () => {
    const tel = detalle?.cliente_telefono || pedido.cliente_telefono;
    if (tel) Linking.openURL(`tel:${tel}`);
  };

  const handleAbrirMapa = () => {
    const dir = detalle?.cliente_direccion || pedido.cliente_direccion;
    if (dir) {
      const encoded = encodeURIComponent(dir);
      Linking.openURL(`https://www.google.com/maps/search/?api=1&query=${encoded}`);
    }
  };

  const handleCompartirRecibo = async () => {
    if (!detalle) return;
    try {
      let config = {};
      try { config = await api.getConfiguracion(); } catch (e) { /* defaults */ }

      // Si no hay logo en la DB, intentar usar el logo local
      if (!config.logo_url) {
        try {
          const logoPath = FileSystem.documentDirectory + 'logo.png';
          const logoExists = await FileSystem.getInfoAsync(logoPath);
          if (logoExists.exists) {
            const base64 = await FileSystem.readAsStringAsync(logoPath, {
              encoding: FileSystem.EncodingType.Base64,
            });
            config.logo_url = `data:image/png;base64,${base64}`;
          }
        } catch (logoError) {
          console.log('No se pudo cargar logo local:', logoError);
        }
      }

      const html = generateTicketHTML(detalle, config, { showInternalId: false });
      const { uri } = await Print.printToFileAsync({ html, base64: false });
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri);
      }
    } catch (error) {
      Alert.alert('Error', 'No se pudo generar el recibo');
    }
  };

  const getEstadoInfo = (estado) => {
    const info = {
      preparado: { color: '#17A2B8', label: 'Preparado - Listo para recoger', icon: 'cube' },
      en_ruta: { color: '#6F42C1', label: 'En Ruta - En camino al cliente', icon: 'car' },
      entregado: { color: '#28A745', label: 'Entregado - Pendiente de completar', icon: 'checkmark-circle' },
      completada: { color: '#28A745', label: 'Completada', icon: 'checkmark-done-circle' },
    };
    return info[estado] || { color: '#999', label: estado, icon: 'help' };
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#6F42C1" />
        <Text style={styles.loadingText}>Cargando detalle del pedido...</Text>
      </View>
    );
  }

  const estado = detalle?.estado || pedido.estado;
  const estadoInfo = getEstadoInfo(estado);

  return (
    <ScrollView style={styles.container} scrollEnabled={!firmando}>
      {/* Estado actual */}
      <Card style={[styles.card, { borderLeftWidth: 4, borderLeftColor: estadoInfo.color }]}>
        <Card.Content style={styles.estadoContainer}>
          <Ionicons name={estadoInfo.icon} size={28} color={estadoInfo.color} />
          <View style={{ marginLeft: 12, flex: 1 }}>
            <Text style={[styles.estadoLabel, { color: estadoInfo.color }]}>{estadoInfo.label}</Text>
            <Text style={styles.pedidoId}>Pedido #{pedido.id}</Text>
          </View>
        </Card.Content>
      </Card>

      {/* Info del cliente */}
      <Card style={styles.card}>
        <Card.Title title="Datos del Cliente" left={() => <Ionicons name="person" size={24} color="#666" />} />
        <Card.Content>
          <Text style={styles.clienteNombre}>{detalle?.cliente_nombre || pedido.cliente_nombre}</Text>

          {(detalle?.cliente_direccion || pedido.cliente_direccion) && (
            <TouchableOpacity style={styles.infoRow} onPress={handleAbrirMapa}>
              <Ionicons name="location" size={18} color="#6F42C1" />
              <Text style={[styles.infoText, { color: '#6F42C1', textDecorationLine: 'underline' }]}>
                {detalle?.cliente_direccion || pedido.cliente_direccion}
              </Text>
              <Ionicons name="open-outline" size={14} color="#6F42C1" />
            </TouchableOpacity>
          )}

          {(detalle?.cliente_telefono || pedido.cliente_telefono) && (
            <TouchableOpacity style={styles.infoRow} onPress={handleLlamar}>
              <Ionicons name="call" size={18} color="#28A745" />
              <Text style={[styles.infoText, { color: '#28A745' }]}>
                {detalle?.cliente_telefono || pedido.cliente_telefono}
              </Text>
              <Ionicons name="open-outline" size={14} color="#28A745" />
            </TouchableOpacity>
          )}

          <View style={styles.infoRow}>
            <Ionicons name="business" size={18} color="#888" />
            <Text style={styles.infoText}>Vendedor: {detalle?.vendedor_nombre || pedido.vendedor_nombre || 'N/A'}</Text>
          </View>

          {detalle?.bodega_nombre && (
            <View style={styles.infoRow}>
              <Ionicons name="cube" size={18} color="#888" />
              <Text style={styles.infoText}>Preparado por: {detalle.bodega_nombre}</Text>
            </View>
          )}
        </Card.Content>
      </Card>

      {/* Productos */}
      {detalle?.detalles && detalle.detalles.length > 0 && (
        <Card style={styles.card}>
          <Card.Title title={`Productos (${detalle.detalles.length})`} left={() => <Ionicons name="list" size={24} color="#666" />} />
          <Card.Content>
            {detalle.detalles.map((item, index) => (
              <View key={item.id}>
                <View style={styles.productoRow}>
                  <View style={styles.productoInfo}>
                    <Text style={styles.productoNombre}>{item.producto_nombre}</Text>
                    <View style={styles.productoDetails}>
                      {item.marca && <Text style={styles.productoMarca}>{item.marca}</Text>}
                      <Text style={styles.productoCodigo}>{item.producto_codigo}</Text>
                    </View>
                  </View>
                  <View style={styles.productoCantidad}>
                    <Text style={styles.cantidadNumero}>{parseFloat(item.cantidad)}</Text>
                    <Text style={styles.cantidadUnidad}>{item.unidad_nombre || 'ud'}</Text>
                  </View>
                  <Text style={styles.productoSubtotal}>
                    L. {parseFloat(item.subtotal || item.cantidad * item.precio_unitario).toFixed(2)}
                  </Text>
                </View>
                {index < detalle.detalles.length - 1 && <Divider style={{ marginVertical: 6 }} />}
              </View>
            ))}

            <Divider style={{ marginVertical: 10 }} />
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>TOTAL</Text>
              <Text style={styles.totalValue}>L. {parseFloat(detalle.total).toFixed(2)}</Text>
            </View>
          </Card.Content>
        </Card>
      )}

      {/* Acciones según estado */}
      {estado === 'preparado' && (
        <Card style={styles.card}>
          <Card.Title title="Recoger Pedido" />
          <Card.Content>
            <Text style={styles.accionDesc}>
              Este pedido fue preparado por bodega y está listo para ser recogido y entregado al cliente.
            </Text>
            <Button
              mode="contained"
              icon="truck-delivery"
              onPress={handleIniciarEntrega}
              style={[styles.accionBtn, { backgroundColor: '#6F42C1' }]}
              loading={processing}
              disabled={processing}
            >
              Recoger y Salir a Entregar
            </Button>
          </Card.Content>
        </Card>
      )}

      {estado === 'en_ruta' && (
        <Card style={styles.card}>
          <Card.Title title="Confirmar Entrega" />
          <Card.Content>
            <Text style={styles.metodoPagoTitle}>Método de Pago:</Text>
            {detalle?.metodo_pago === 'credito' ? (
              <View style={styles.creditoLockBox}>
                <Ionicons name="lock-closed" size={18} color="#e67e22" />
                <View style={{ flex: 1, marginLeft: 8 }}>
                  <Text style={styles.creditoLockTitle}>Venta a Crédito</Text>
                  <Text style={styles.creditoLockText}>
                    Esta venta fue registrada a crédito por el vendedor. El cobro se gestiona
                    desde "Cuentas por Cobrar" — no se suma a las ventas del día.
                  </Text>
                </View>
              </View>
            ) : (
              <RadioButton.Group onValueChange={setMetodoPago} value={metodoPago}>
                <View style={styles.radioRow}>
                  <RadioButton.Item label="Efectivo" value="efectivo" style={styles.radioItem} />
                  <RadioButton.Item label="Transferencia" value="transferencia" style={styles.radioItem} />
                </View>
                <View style={styles.radioRow}>
                  <RadioButton.Item label="Tarjeta" value="tarjeta" style={styles.radioItem} />
                </View>
              </RadioButton.Group>
            )}

            <TextInput
              label="Notas de entrega (opcional)"
              value={notasEntrega}
              onChangeText={setNotasEntrega}
              style={styles.notasInput}
              multiline
              numberOfLines={2}
            />

            {/* Firma digital del cliente */}
            <Text style={styles.firmaTitle}>Firma del Cliente:</Text>
            {!firmaCapturada ? (
              <View style={styles.firmaContainer}>
                <SignaturePad
                  ref={signatureRef}
                  height={200}
                  onSignature={handleFirmaRecibida}
                  onClear={() => { setFirmaData(null); setFirmaCapturada(false); }}
                  onDrawingChange={setFirmando}
                />
                <Button
                  mode="outlined"
                  icon="pencil"
                  onPress={handleCapturarFirma}
                  style={styles.capturarFirmaBtn}
                >
                  Capturar Firma
                </Button>
              </View>
            ) : (
              <View style={styles.firmaPreview}>
                <View style={styles.firmaPreviewHeader}>
                  <View style={styles.firmaCheckRow}>
                    <Ionicons name="checkmark-circle" size={20} color="#28A745" />
                    <Text style={styles.firmaCheckText}>Firma capturada</Text>
                  </View>
                  <Button
                    mode="text"
                    compact
                    onPress={() => { setFirmaData(null); setFirmaCapturada(false); }}
                    labelStyle={{ fontSize: 12, color: '#DC3545' }}
                  >
                    Repetir
                  </Button>
                </View>
                {firmaData && (
                  <Image
                    source={{ uri: firmaData }}
                    style={styles.firmaImage}
                    resizeMode="contain"
                  />
                )}
              </View>
            )}

            <Button
              mode="contained"
              icon="check-circle"
              onPress={handleConfirmarEntrega}
              style={[styles.accionBtn, { backgroundColor: firmaCapturada ? '#28A745' : '#999' }]}
              loading={processing}
              disabled={processing}
            >
              {firmaCapturada ? 'Confirmar Entrega al Cliente' : 'Capture la firma primero'}
            </Button>
          </Card.Content>
        </Card>
      )}

      {estado === 'entregado' && (
        <Card style={styles.card}>
          <Card.Title title="Completar Venta" />
          <Card.Content>
            <View style={styles.entregadoInfo}>
              <Ionicons name="checkmark-circle" size={24} color="#28A745" />
              <Text style={styles.entregadoText}>
                Entregado - Método de pago: {detalle?.metodo_pago || 'N/A'}
              </Text>
            </View>

            {/* Mostrar firma guardada */}
            {detalle?.firma_cliente && (
              <View style={styles.firmaGuardada}>
                <Text style={styles.firmaGuardadaLabel}>Firma del cliente:</Text>
                <Image
                  source={{ uri: detalle.firma_cliente }}
                  style={styles.firmaImageGuardada}
                  resizeMode="contain"
                />
              </View>
            )}

            <Button
              mode="contained"
              icon="check-all"
              onPress={handleCompletarVenta}
              style={[styles.accionBtn, { backgroundColor: '#007BFF' }]}
              loading={processing}
              disabled={processing}
            >
              Marcar como Completada
            </Button>
          </Card.Content>
        </Card>
      )}

      {estado === 'completada' && (
        <Card style={[styles.card, { backgroundColor: '#D4EDDA' }]}>
          <Card.Content style={styles.completadaContainer}>
            <Ionicons name="checkmark-done-circle" size={40} color="#28A745" />
            <Text style={styles.completadaText}>Venta completada exitosamente</Text>
            {detalle?.metodo_pago && (
              <Text style={styles.completadaMetodo}>Método de pago: {detalle.metodo_pago}</Text>
            )}
            {detalle?.repartidor_entregado_en && (
              <Text style={styles.completadaFecha}>
                Entregado: {new Date(detalle.repartidor_entregado_en).toLocaleString('es-HN')}
              </Text>
            )}
          </Card.Content>
        </Card>
      )}

      {/* Compartir recibo en estados finales */}
      {(estado === 'entregado' || estado === 'completada') && (
        <Button
          mode="outlined"
          icon="share"
          onPress={handleCompartirRecibo}
          style={{ marginHorizontal: 8, marginBottom: 8, borderColor: '#007BFF' }}
          textColor="#007BFF"
        >
          Compartir Recibo
        </Button>
      )}

      {/* Mostrar firma en estados completados */}
      {(estado === 'completada') && detalle?.firma_cliente && (
        <Card style={styles.card}>
          <Card.Title title="Firma del Cliente" left={() => <Ionicons name="pencil" size={24} color="#666" />} />
          <Card.Content>
            <Image
              source={{ uri: detalle.firma_cliente }}
              style={styles.firmaImageGuardada}
              resizeMode="contain"
            />
            {detalle?.firma_cliente_en && (
              <Text style={styles.firmaFecha}>
                Firmado: {new Date(detalle.firma_cliente_en).toLocaleString('es-HN')}
              </Text>
            )}
          </Card.Content>
        </Card>
      )}

      <View style={{ height: 30 }} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5', padding: 8 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 10, color: '#666' },
  card: { marginBottom: 8, elevation: 2 },
  // Estado
  estadoContainer: { flexDirection: 'row', alignItems: 'center' },
  estadoLabel: { fontSize: 14, fontWeight: '600' },
  pedidoId: { fontSize: 18, fontWeight: 'bold', color: '#333', marginTop: 2 },
  // Cliente
  clienteNombre: { fontSize: 18, fontWeight: 'bold', color: '#333', marginBottom: 8 },
  infoRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6 },
  infoText: { fontSize: 14, color: '#666', marginLeft: 8, flex: 1 },
  // Productos
  productoRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8 },
  productoInfo: { flex: 1, marginRight: 8 },
  productoNombre: { fontSize: 14, fontWeight: '600', color: '#333' },
  productoDetails: { flexDirection: 'row', marginTop: 2 },
  productoMarca: { fontSize: 12, color: '#17A2B8', marginRight: 8 },
  productoCodigo: { fontSize: 12, color: '#999' },
  productoCantidad: { alignItems: 'center', marginRight: 12, minWidth: 40 },
  cantidadNumero: { fontSize: 16, fontWeight: 'bold', color: '#333' },
  cantidadUnidad: { fontSize: 10, color: '#888' },
  productoSubtotal: { fontSize: 14, fontWeight: '600', color: '#28a745', minWidth: 70, textAlign: 'right' },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  totalLabel: { fontSize: 16, fontWeight: 'bold', color: '#333' },
  totalValue: { fontSize: 20, fontWeight: 'bold', color: '#28a745' },
  // Acciones
  accionDesc: { fontSize: 13, color: '#666', marginBottom: 12 },
  accionBtn: { marginTop: 12 },
  metodoPagoTitle: { fontSize: 14, fontWeight: '600', color: '#333', marginBottom: 4 },
  radioRow: { flexDirection: 'row' },
  radioItem: { flex: 1 },
  notasInput: { marginTop: 8, marginBottom: 12, backgroundColor: '#fff' },
  // Crédito (lock box mostrado al repartidor)
  creditoLockBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FFF3E0',
    borderLeftWidth: 4,
    borderLeftColor: '#e67e22',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  creditoLockTitle: { fontSize: 14, fontWeight: '700', color: '#e67e22', marginBottom: 4 },
  creditoLockText: { fontSize: 12, color: '#7a4a13', lineHeight: 16 },
  // Firma
  firmaTitle: { fontSize: 14, fontWeight: '600', color: '#333', marginBottom: 8 },
  firmaContainer: { marginBottom: 4 },
  capturarFirmaBtn: { marginTop: 8, borderColor: '#6F42C1' },
  firmaPreview: { borderWidth: 1, borderColor: '#28A745', borderRadius: 8, overflow: 'hidden', marginBottom: 4 },
  firmaPreviewHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 6, backgroundColor: '#F0FFF0' },
  firmaCheckRow: { flexDirection: 'row', alignItems: 'center' },
  firmaCheckText: { fontSize: 13, color: '#28A745', fontWeight: '600', marginLeft: 6 },
  firmaImage: { width: '100%', height: 120, backgroundColor: '#fff' },
  // Firma guardada
  firmaGuardada: { marginVertical: 8 },
  firmaGuardadaLabel: { fontSize: 13, color: '#666', marginBottom: 4 },
  firmaImageGuardada: { width: '100%', height: 150, backgroundColor: '#fff', borderWidth: 1, borderColor: '#EEE', borderRadius: 8 },
  firmaFecha: { fontSize: 12, color: '#888', marginTop: 4, textAlign: 'center' },
  // Entregado
  entregadoInfo: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  entregadoText: { fontSize: 14, color: '#155724', marginLeft: 8 },
  // Completada
  completadaContainer: { alignItems: 'center', padding: 16 },
  completadaText: { fontSize: 16, color: '#155724', fontWeight: 'bold', marginTop: 8 },
  completadaMetodo: { fontSize: 13, color: '#155724', marginTop: 4 },
  completadaFecha: { fontSize: 12, color: '#666', marginTop: 4 },
});

export default DetalleEntregaScreen;
